import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useUrlState — bind a piece of component state to a URL search param.
 *
 * Returns [value, setValue] with the same shape as useState. Reading
 * pulls from `?key=` in the current URL; writing updates the URL via
 * router navigation. Defaults are omitted from the URL so an unfiltered
 * page stays clean (no `?foo=&bar=` noise).
 *
 * Updates use `replace: true` so rapid filter toggling doesn't pollute
 * browser history — back-button still escapes the page in one step.
 *
 * setValue is referentially stable across renders, so it's safe to
 * include in useEffect/useCallback dependency arrays without causing
 * loops.
 *
 * @param {string} key                URL param name
 * @param {*}      defaultValue       value when param is absent / invalid
 * @param {object} options
 * @param {(s:string)=>any} options.parse        decode string → value (default identity)
 * @param {(v:any)=>string} options.serialize    encode value → string (default String)
 * @param {(v:any)=>boolean} options.isDefault   if true, param is omitted from URL
 */
export function useUrlState(key, defaultValue, options) {
  // Stash the latest defaultValue/options behind a ref so setValue can
  // read them without those values participating in its useCallback
  // deps. Without this, inline default options (e.g. `useUrlState('x',
  // null)` where options is undefined and defaults are constructed
  // inside the hook) would make setValue a new reference every render,
  // and any useEffect that depends on setValue would loop.
  const latestRef = useRef();
  latestRef.current = { defaultValue, options };

  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(() => {
    const { defaultValue: dv, options: opt } = latestRef.current;
    const parse = opt?.parse || identity;
    const raw = searchParams.get(key);
    if (raw == null || raw === '') return dv;
    try {
      const parsed = parse(raw);
      return parsed == null ? dv : parsed;
    } catch {
      return dv;
    }
  }, [searchParams, key]);

  const setValue = useCallback((next) => {
    setSearchParams((prev) => {
      const { defaultValue: dv, options: opt } = latestRef.current;
      const parse = opt?.parse || identity;
      const serialize = opt?.serialize || stringify;
      const isDefault = opt?.isDefault || makeIsDefault(dv);

      const params = new URLSearchParams(prev);
      const resolved = typeof next === 'function'
        ? next(parseOr(parse, prev.get(key), dv))
        : next;
      if (isDefault(resolved)) {
        params.delete(key);
      } else {
        params.set(key, serialize(resolved));
      }
      return params;
    }, { replace: true });
  }, [setSearchParams, key]);

  return [value, setValue];
}

function identity(s) { return s; }
function stringify(v) { return v == null ? '' : String(v); }
function makeIsDefault(dv) {
  return (v) => v === dv || v == null || v === '';
}
function parseOr(parse, raw, fallback) {
  if (raw == null || raw === '') return fallback;
  try {
    const v = parse(raw);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
}

/* Common parsers/serializers for booleans encoded as 1/0 in the URL. */
export const boolUrlParser = {
  parse: (s) => s === '1' || s === 'true',
  serialize: (v) => (v ? '1' : '0'),
};
